import { values } from 'lodash';
import { CartStep, OptionalAssets } from '@monorepo/types';

export const PURCHASE_STEP_PARAM = 'step';
export const PURCHASE_ASSET_PARAM = 'asset';
export const PURCHASE_GROUP_PARAM = 'group';
export const PURCHASE_SOURCE_PARAM = 'source';
export const QUICK_PURCHASE_SOURCE = 'quick_purchase';

/**
 * Returns true when the current URL indicates the user arrived via the
 * quick-purchase flow (`?source=quick_purchase`).
 */
export const isQuickPurchaseSource = (searchParams: URLSearchParams): boolean =>
  searchParams.get(PURCHASE_SOURCE_PARAM) === QUICK_PURCHASE_SOURCE;

/**
 * Convert URL parameter to CartStep enum
 */
export const urlParamToStep = (param: string | null): CartStep | null => {
  if (!param) return null;

  // Check if the parameter matches any CartStep enum value
  const validSteps = Object.values(CartStep);
  if (validSteps.includes(param as CartStep)) {
    return param as CartStep;
  }

  return null;
};

/**
 * Validate if step parameter is a valid CartStep
 */
export const isValidStep = (step: string | null): step is CartStep => {
  if (!step) return false;
  return values(CartStep).includes(step as CartStep);
};

/**
 * Get default step for fallback scenarios
 */
export const getDefaultStep = (): CartStep => {
  return CartStep.Assets;
};

/**
 * Check if step requires asset selection
 */
export const isAssetStep = (step: CartStep): boolean => {
  return step === CartStep.Assets;
};

/**
 * Check if step is a program selection step
 */
export const isProgramStep = (step: CartStep): boolean => {
  return step === CartStep.Programs;
};

/**
 * Check if step is the billing step
 */
export const isBillingStep = (step: CartStep): boolean => {
  return step === CartStep.Billing;
};

/**
 * Get all valid step values for validation
 */
export const getAllValidSteps = (): CartStep[] => {
  return Object.values(CartStep);
};

/**
 * Convert URL parameter to OptionalAssets enum
 */
export const urlParamToAsset = (
  param: string | null,
): OptionalAssets | undefined => {
  // Check if the parameter matches any OptionalAssets enum value
  const validAssets = values(OptionalAssets);
  if (validAssets.includes(param as OptionalAssets)) {
    return param as OptionalAssets;
  }
};

/**
 * Validate if asset parameter is a valid OptionalAssets
 */
export const isValidAsset = (asset: string | null): asset is OptionalAssets => {
  if (!asset) return false;
  return values(OptionalAssets).includes(asset as OptionalAssets);
};

/**
 * Check if step transition is valid (from one step to another)
 */
export const isValidStepTransition = (
  fromStep: CartStep,
  toStep: CartStep,
): boolean => {
  // Allow any transition to/from Assets
  if (fromStep === CartStep.Assets || toStep === CartStep.Assets) {
    return true;
  }

  // Programs can transition to billing
  if (isProgramStep(fromStep) && isBillingStep(toStep)) {
    return true;
  }

  // Billing can transition back to programs
  if (isBillingStep(fromStep) && isProgramStep(toStep)) {
    return true;
  }

  return false;
};

/**
 * Check if a step requires an asset parameter
 */
export const stepRequiresAsset = (step: CartStep): boolean => {
  return step === CartStep.Programs || step === CartStep.Billing;
};

export const validateQuickBuy = (
  step: CartStep,
  asset?: OptionalAssets,
): boolean => {
  return asset === OptionalAssets.QuickBuy ? isBillingStep(step) : true;
};
